import { Context } from '@front10/helpers/dist/Context';
import getStyles from '@front10/helpers/dist/UseStyles';
import React, { ChangeEvent, ClipboardEvent, KeyboardEvent, RefObject } from 'react';
import { replaceAll } from '../../../helpers/utils';
import Input from '../../elements/Input';
import { InputProps } from '../../elements/Input/Input';
import formatMoney from './utils';

export enum LabelPositionOptions {
  left = 'left',
  right = 'right',
  none = 'none'
}
interface Props {
  /**
   * labelKey
   */
  labelKey: string;
  /**
   * Set placeholder value */
  placeholderKey: string;
  /**
   * Set Clear Button label*/
  ariaLabelIconCloseKey: string;
  /**
   * Set label for Left Aria Label */
  leftAriaLabelKey: string;
  /**
   * Set label for Aria Description */
  ariaLabelDescriptionKey: string;
  /**
   * Set label for Invalid Aria Description */
  ariaLabelInvalidDescriptionKey: string;
  /**
   * Show clear button
   */
  showClear: boolean;
  /**
   * Currency code
   */
  currencyCode: string;
  /**
   * Currency code
   */
  currencyToDisplay: string;
  /**
   * decimal separator in the budget
   */
  decimalSeparator: string;
  /**
   * decimalPlaces in the budget
   */
  decimalPlaces: number;
  /**
   * thousand separator in the budget
   */
  thousandSeparator: string;
  /**
   * thousandsPlaces in the budget
   */
  thousandsPlaces: number;
  /**
   * Value of the budget
   */
  defaultValue: any;
  /**
   * onChange function
   */
  onChange?: (value?: number | string) => void;
  /**
   * onClear input function
   */
  onClear?: () => void;
  /**
   * Patter to format date
   */
  focusReferences: $TSFixMe;
  /**
   * Delay update in ms
   */
  updateDelay: number;
  /**
   *  string: Define label position (left,right,none)
   */
  innerLabelPosition: LabelPositionOptions;
  /**
   * min value allowed
   */
  minValue: number;
  /**
   * Max value allowed
   */
  maxValue: number;
}

export type BasicBudgetProps = Partial<
  Omit<InputProps, 'onChange' | 'onClear' | 'focusReferences'>
> &
  Props;

interface State {
  rightIcons: string[];
  value: string | number;
  formatterValue: string | null;
  invalidFormat: boolean;
  rightAriaLabel: string[];
}

type InputElement = HTMLInputElement & HTMLTextAreaElement;

class Budget extends React.Component<BasicBudgetProps, State> {
  static defaultProps = {
    labelKey: 'cmp-budge-label',
    placeholderKey: 'cmp-budge-placeholder',
    ariaLabelIconCloseKey: 'cmp-budge-aria-label-icon-close',
    leftAriaLabelKey: 'cmp-budge-aria-label-icon-coins',
    ariaLabelDescriptionKey: 'cmp-budge-label-description',
    ariaLabelInvalidDescriptionKey: 'cmp-budge-label-invalid-description',
    leftIcons: ['coins'],
    showClear: true,
    currencyCode: 'USD',
    currencyToDisplay: '',
    decimalSeparator: '.',
    thousandSeparator: ',',
    decimalPlaces: 2,
    thousandsPlaces: 3,
    defaultValue: '',
    minValue: 1,
    maxValue: 10_000_000_000,
    leftIconsProps: {
      color: 'transparent'
    },
    rightLabelsProps: {
      margin: '2',
      display: 'flex',
      alignItems: 'center',
      bgColor: 'light',
      paddingWidth: '1'
    },
    leftLabelsProps: {
      margin: '2',
      display: 'flex',
      alignItems: 'center',
      bgColor: 'light',
      paddingWidth: '1'
    },
    leftLabelsPropsContainer: {},
    rightLabelsPropsContainer: {},
    focusReferences: () => {},
    updateDelay: 500,
    innerLabelPosition: LabelPositionOptions.left
  };

  static contextType = Context;

  static componentName = 'Budget';

  inputFocus: RefObject<$TSFixMe>;
  debounce: $TSFixMe = null;
  lastValue: number | string = '';
  currency: string;

  constructor(props: BasicBudgetProps) {
    super(props);
    let newDefaultValue: any = '';
    if (props.defaultValue) {
      if (typeof props.defaultValue === 'string' || typeof props.defaultValue === 'number') {
        newDefaultValue = props.defaultValue;
      } else {
        if (typeof props.defaultValue === 'object') {
          if (Object.keys(props.defaultValue).length !== 0) {
            newDefaultValue = props.defaultValue?.maximum ? props.defaultValue?.maximum : '';
          } else {
            newDefaultValue = '';
          }
        }
      }
    }
    this.state = {
      rightIcons: this.getRightIcons(newDefaultValue),
      value: newDefaultValue ? newDefaultValue : '',
      // TODO: Double check this state (formatterValue seems to be derived state that hast to be kept in sync with value).
      // Check if this should have a single source of truth.
      formatterValue: formatMoney(
        newDefaultValue,
        props.decimalPlaces,
        props.thousandSeparator,
        props.decimalSeparator
      ),
      invalidFormat: false,
      rightAriaLabel: []
    };

    this.inputFocus = React.createRef<$TSFixMe>();
    this.lastValue = newDefaultValue;
    if (typeof props.focusReferences === 'function') {
      props.focusReferences(this);
    }
    this.currency = Boolean(props.currencyToDisplay) ? props.currencyToDisplay : props.currencyCode;
  }

  componentDidMount() {
    const { defaultValue } = this.props;
    this.setState({ rightAriaLabel: this.getRightAriaLabels(defaultValue) });
  }

  componentDidUpdate(prevProps: BasicBudgetProps) {
    if (this.props.currencyToDisplay !== prevProps.currencyToDisplay) {
      this.setState({ formatterValue: '', rightIcons: [] });
      this.currency = Boolean(this.props.currencyToDisplay)
        ? this.props.currencyToDisplay
        : this.props.currencyCode;
    }
  }

  getRightIcons = (value: number | string) => {
    let icons: ['close'] | [] = [];
    if (value) {
      icons = ['close'];
    }
    return icons;
  };

  getRightAriaLabels = (value: number | string) => {
    const { translate } = this.context;
    let labels: string[] = [];
    if (value) {
      labels = [translate(this.props.ariaLabelIconCloseKey, 'Clear')];
    }
    return labels;
  };

  isValidValue = (value: string) => {
    // Do not allow to type 0 as a budget. I.e. You are allowed to type
    // 103, 980, heck even 007 (as long as you type the 7 first),
    // but not 0 alone. https://everymundo.atlassian.net/browse/FC-2498
    const regex = /^[0-9]*[1-9][0-9]*/;
    if (!regex.test(value)) {
      return false;
    }
    let number = Number.parseInt(value, 10);
    if (number < this.props.minValue || number > this.props.maxValue) {
      return false;
    }
    return true;
  };

  handleKeyPress = (event: KeyboardEvent<InputElement>) => {
    // The global replaceAll generate a Regex with the find parameter (thousandSeparator),
    // so if the thousandSeparator is a dot, needs to be be escaped.
    // Another possible fix is use the replaceAll of string, this one don't generate a Regex.
    // like: event.currentTarget.value.replaceAll(this.props.thousandSeparator, '') + event.key
    const value =
      replaceAll(
        event.currentTarget.value,
        this.props.thousandSeparator === '.' ? '\\.' : this.props.thousandSeparator,
        ''
      ) + event.key;
    if (!this.isValidValue(value)) {
      event.preventDefault();
    }
  };

  handleKeyDown = (event: KeyboardEvent<InputElement>) => {
    const { onClear, onKeyDown, defaultValue } = this.props;
    if (event.currentTarget.value === '' && event.key === 'Backspace' && defaultValue !== null) {
      onClear?.();
    }
    onKeyDown?.(event);
  };

  handlePaste = (event: ClipboardEvent<InputElement>) => {
    const value = (event.clipboardData || window.clipboardData).getData('text');
    if (!this.isValidValue(value)) {
      event.preventDefault();
    }
  };

  setValue = (currentValue: $TSFixMe, event?: $TSFixMe) => {
    const { onChange, decimalPlaces, thousandSeparator, decimalSeparator } = this.props;
    if (event && currentValue.startsWith('0')) {
      // TODO: Check if this is a valid thing to do here and leave
      // an explanation if so.
      // eslint-disable-next-line no-param-reassign
      event.target.value = '';
      event.preventDefault();
      return;
    }
    let invalid = false;
    let formatterValue = '';
    let { value } = this.state;
    if (currentValue !== '') {
      const currenValueReplaced = currentValue
        .replaceAll(thousandSeparator, '')
        .replaceAll(decimalSeparator, '');
      const currentValueInt = parseFloat(currenValueReplaced);
      const currentValueFormatter = formatMoney(
        currentValueInt,
        decimalPlaces,
        thousandSeparator,
        decimalSeparator
      );
      if (!currentValueFormatter) {
        invalid = true;
      } else {
        formatterValue = currentValueFormatter;
        value = currentValueInt;
      }
    } else {
      formatterValue = '';
      invalid = false;
      value = '';
    }
    this.setState(
      {
        formatterValue,
        rightIcons: this.getRightIcons(formatterValue),
        rightAriaLabel: this.getRightAriaLabels(formatterValue),
        invalidFormat: invalid
      },
      () => {
        if (this.props.updateDelay) {
          clearTimeout(this.debounce);
          this.debounce = setTimeout(() => {
            if (!invalid) {
              onChange?.(value);
            }
          }, this.props.updateDelay);
        } else {
          if (!invalid) {
            onChange?.(value);
          }
        }
      }
    );
  };

  handleInputValue = (event: ChangeEvent<InputElement>) => {
    const currentValue = event.target.value;
    this.setValue(currentValue, event);
  };

  reset = () => {
    this.setState({
      value: '',
      formatterValue: '',
      rightIcons: this.getRightIcons(''),
      rightAriaLabel: this.getRightAriaLabels('')
    });
    this.lastValue = '';
    this.inputFocus.current.focus();
  };

  focusedElement = () => {
    this.inputFocus.current?.focus();
  };

  update = (value: any) => {
    const { decimalPlaces, thousandSeparator, decimalSeparator } = this.props;
    this.setState({
      value: value,
      formatterValue: formatMoney(value, decimalPlaces, thousandSeparator, decimalSeparator)
    });
  };

  handleRightClick = (icon: string) => {
    const { onClear } = this.props;
    if (icon === 'close') {
      this.setState(
        {
          value: '',
          formatterValue: '',
          rightIcons: this.getRightIcons(''),
          rightAriaLabel: this.getRightAriaLabels('')
        },
        () => {
          this.lastValue = '';
          onClear?.();
        }
      );
      this.inputFocus.current.focus();
    }
  };

  getInputSettings = () => {
    const { innerLabelPosition, showClear, leftIcons, rightIcons } = this.props;
    const { rightIcons: clearIcon } = this.state;
    const { translate } = this.context;
    let settings = {};
    if (innerLabelPosition === LabelPositionOptions.right) {
      settings = {
        rightLabels: [translate(`cmp-currency-${this.currency}`, this.currency)],
        leftLabels: [],
        leftIcons: leftIcons,
        rightIcons: showClear ? clearIcon : rightIcons
      };
    } else if (innerLabelPosition === LabelPositionOptions.left) {
      settings = {
        rightLabels: [],
        leftLabels: [translate(`cmp-currency-${this.currency}`, this.currency)],
        leftIcons: [],
        rightIcons: showClear ? clearIcon : rightIcons
      };
    } else if (innerLabelPosition === LabelPositionOptions.none) {
      settings = {
        rightLabels: [],
        leftLabels: [],
        rightIcons: showClear ? clearIcon : rightIcons
      };
    }
    return settings;
  };

  render() {
    const {
      showClear,
      labelKey,
      placeholderKey,
      leftAriaLabelKey,
      ariaLabelDescriptionKey,
      ariaLabelInvalidDescriptionKey,
      onRightIconClick
    } = this.props;
    const { rightAriaLabel, invalidFormat, formatterValue } = this.state;
    const { translate, direction, theme } = this.context;
    const [className, style] = getStyles(this.props, { direction, theme });
    const inputSettings = this.getInputSettings();

    this.props.rightLabelsPropsContainer.onClick = onRightIconClick;
    this.props.leftLabelsPropsContainer.onClick = onRightIconClick;

    return (
      <Input
        type="text"
        inputMode="numeric"
        placeholder={translate(placeholderKey, 'Any budget')}
        label={translate(labelKey, 'Budget')}
        autoComplete="off"
        {...this.props}
        dataTestId="budget"
        name="budget"
        className={`fr-budget ${className}`}
        style={style}
        onChange={this.handleInputValue}
        onKeyPress={this.handleKeyPress}
        onKeyDown={this.handleKeyDown}
        onPaste={this.handlePaste}
        value={formatterValue || ''}
        invalid={invalidFormat}
        ariaInvalid={invalidFormat}
        onRightIconClick={showClear ? this.handleRightClick : onRightIconClick}
        rightAriaLabel={rightAriaLabel}
        leftTabIndex={[-1]}
        leftAriaLabel={[translate(leftAriaLabelKey, 'Coins')]}
        focusReferences={this.inputFocus}
        ariaLabelDescription={translate(
          `${ariaLabelDescriptionKey}-${this.currency.trim()}`,
          'Enter the best USD budget'
        )}
        ariaLabelInvalidDescription={translate(
          ariaLabelInvalidDescriptionKey,
          'Invalid format, please change the value'
        )}
        {...inputSettings}
        maxLength={undefined}
      />
    );
  }
}

export default Budget;
